// Recorded video that runs on without a break: the alarm clip, then whatever NX recorded after it, then live, played as
// one stream through Media Source (the user's requirement, 2026-10-27: "after the alarm it should continue to play
// continuous video ... must be fluid"; going live when the clip ran out skipped the seconds in between).
//
// NX ends a recorded stream at what it had written when the stream was opened, about 5 s behind the present. So when one
// stream ends the player asks for more on the same ticket (core /api/video/media/:ticket): the recording from where its
// buffer ends (?from=) while that is more than a minute back, else the live stream (?live=1), with the recording up to
// the live stream's first frame filled in first. mp4.ts puts every fragment on one timeline by NX's own times, so
// joins are exact: a fragment already buffered is skipped, and one that starts a little earlier overwrites the same
// frames. Playback stays as far behind live as the clip started; the operator jumps to live with Live (L).
//
// A quad tile that isn't the main camera plays NX's low-resolution stream (four full-size streams were more than one
// site's uplink carried, staging 2026-10-28) and goes on into live like the main camera, kept to the timeline's playhead
// (followStep; the user, 2026-10-28: quad tiles "follow the timeline"). `live: false` ends a stream at the present.
//
// Reading pauses while 30 s are buffered ahead (TCP holds NX back), and video more than 30 s behind the playhead is
// dropped. A stream that breaks is carried on from where the buffer ends; after repeated failures the player ends the
// stream, and the console's 'ended' handling takes over (view.ts continueAfter).

import { BoxSplitter, parseInit, placeFragment, type Init } from './mp4.ts';

/** When a stream ends this far behind the present, more recording follows; nearer, live. */
export const FOLLOW_ARCHIVE_MS = 60_000;
const MAX_AHEAD_S = 30;
const KEEP_BEHIND_S = 30;
/** A fragment that ends within this of what is buffered adds nothing. */
const JOIN_SLACK_MS = 50;
const RETRIES = 3;
/** Stuck this long (not paused, nothing to play) and the player gives up. */
const STUCK_MS = 20_000;

export interface ContinuousSource {
  /** The session's stream URL. */
  first: string;
  /** The URL that carries it on, on the current ticket. */
  follow: (q: { from: number } | { live: true }) => string;
  /** Replaces the ticket with a fresh one for the same view (the first is good for 10 minutes). */
  renew: () => Promise<void>;
  /** Carries on into live once the recording reaches the present (default); false: ends there. */
  live?: boolean;
}

export interface ContinuousHooks {
  /** This browser can't play the stream this way: play the plain URL instead. */
  unsupported: () => void;
  /** The first stream failed, and why. */
  failed: (message: string) => void;
  /** The player is following the live stream (true) or playing recording (false). */
  following?: (live: boolean) => void;
}

/** Off the timeline's playhead by more than this, a tile jumps to it (when that is buffered). */
const FOLLOW_JUMP_S = 3;
/** Within this of the playhead, a tile plays at normal speed. */
const FOLLOW_SLACK_S = 0.25;

/**
 * How a quad tile keeps to the timeline's playhead: `driftS` is how far its video is ahead of the playhead (negative:
 * behind), at video time `t`. Far off, it jumps to the playhead if that is buffered; otherwise it plays a little faster
 * or slower until it is back within a quarter second (a jump would stall it while the buffer refills).
 */
export function followStep(driftS: number, t: number, buffered: (s: number) => boolean): { seekTo?: number; rate: number } {
  const target = t - driftS;
  if (Math.abs(driftS) > FOLLOW_JUMP_S && buffered(target)) return { seekTo: target, rate: 1 };
  if (driftS < -FOLLOW_SLACK_S) return { rate: driftS < -1 ? 1.5 : 1.1 };
  if (driftS > FOLLOW_SLACK_S) return { rate: driftS > 1 ? 0.5 : 0.9 };
  return { rate: 1 };
}

class HttpFail extends Error {}
class Unsupported extends Error {}

type Item = { kind: 'init'; mime: string; bytes: Uint8Array } | { kind: 'frag'; bytes: Uint8Array; startMs: number; endMs: number };

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const concat = (...parts: Uint8Array[]) => {
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let o = 0;
  for (const p of parts) {
    out.set(p, o);
    o += p.length;
  }
  return out;
};

export class ContinuousPlayer {
  private readonly ms = new MediaSource();
  private readonly ac = new AbortController();
  private sb: SourceBuffer | null = null;
  private mime = '';
  /** Wall time (epoch ms) of the video's time 0: the first stream's start. */
  private anchorMs: number | null = null;
  /** Wall time up to which video has been buffered. */
  private end = 0;
  private stopped = false;
  /** Gave up following: plays out what is buffered. */
  private done = false;
  private stuckSince: number | null = null;
  private seeked = false;
  private timer: ReturnType<typeof setInterval> | undefined;
  private objectUrl = '';

  constructor(
    private readonly video: HTMLVideoElement,
    private readonly atMs: number,
    private readonly src: ContinuousSource,
    private readonly hooks: ContinuousHooks,
  ) {}

  static supported(): boolean {
    return typeof window !== 'undefined' && typeof window.MediaSource !== 'undefined';
  }

  /** The recording time (epoch ms) at a video time. */
  wallAt(t: number): number {
    return (this.anchorMs ?? this.atMs) + t * 1000;
  }

  start(): void {
    this.objectUrl = URL.createObjectURL(this.ms);
    this.video.src = this.objectUrl;
    this.video.play().catch(() => undefined);
    this.timer = setInterval(() => this.stepGaps(), 500);
    void this.run().catch((e: unknown) => this.giveUp(e));
  }

  stop(): void {
    this.stopped = true;
    this.ac.abort();
    clearInterval(this.timer);
    this.video.removeAttribute('src');
    this.video.load();
    URL.revokeObjectURL(this.objectUrl);
  }

  private async run(): Promise<void> {
    if (this.ms.readyState !== 'open') await new Promise((r) => this.ms.addEventListener('sourceopen', r, { once: true }));
    await this.stream(() => this.src.first, (it) => this.append(it), true);
    // Nothing in it (no recording there, or nothing this player could place): the plain <video> decides.
    if (this.end === 0) throw new Unsupported('the first stream had no video');
    let tries = 0;
    while (!this.stopped && !this.done) {
      const before = this.end;
      try {
        // Far back: the recording from here (when nothing more was recorded, live). Near the present: live.
        if (Date.now() - this.end > FOLLOW_ARCHIVE_MS && (await this.fill())) continue;
        if (this.src.live === false) return this.finish();
        await this.live();
      } catch (e) {
        if (this.stopped || e instanceof Unsupported || ++tries > RETRIES) throw e;
        await sleep(2000);
        continue;
      }
      if (this.end > before) tries = 0;
      else if (++tries > RETRIES) throw new Error('no more video from NX');
    }
  }

  /** The recording from where the buffer ends; true when it added video. */
  private async fill(): Promise<boolean> {
    const before = this.end;
    const from = Math.floor(this.end);
    await this.stream(() => this.src.follow({ from }), (it) => this.append(it), false);
    return this.end > before;
  }

  /** The live stream, held back until the recording before its first frame is buffered. */
  private async live(): Promise<void> {
    const queue: Item[] = [];
    let direct = false;
    let liveStart: number | null = null;
    let first: () => void = () => undefined;
    const gotFirst = new Promise<void>((r) => (first = r));
    const run = this.stream(
      () => this.src.follow({ live: true }),
      async (it) => {
        if (it.kind === 'frag' && liveStart === null) {
          liveStart = it.startMs;
          first();
        }
        if (direct) await this.append(it);
        else queue.push(it);
      },
      false,
    ).finally(() => first());
    run.catch(() => undefined); // awaited below; a failure before then is not unhandled
    await gotFirst;
    // NX writes the recording a few seconds behind the present, so this can take a few rounds.
    let idle = 0;
    while (!this.stopped && liveStart !== null && this.end < liveStart - JOIN_SLACK_MS && idle < RETRIES) {
      if (await this.fill()) idle = 0;
      else {
        idle++;
        await sleep(1000);
      }
    }
    while (queue.length) await this.append(queue.shift()!);
    direct = true;
    this.hooks.following?.(true);
    try {
      await run;
    } finally {
      this.hooks.following?.(false);
    }
  }

  /** Reads one stream into `sink`, init segment first; on 401 for a carried-on stream, renews the ticket once. */
  private async stream(url: () => string, sink: (it: Item) => Promise<void>, first: boolean): Promise<void> {
    let r = await fetch(url(), { signal: this.ac.signal, cache: 'no-store' });
    if (r.status === 401 && !first) {
      await r.body?.cancel();
      await this.src.renew();
      r = await fetch(url(), { signal: this.ac.signal, cache: 'no-store' });
    }
    if (!r.ok) {
      const body = (await r.json().catch(() => null)) as { error?: string } | null;
      throw new HttpFail(body?.error ? `${body.error.charAt(0).toUpperCase()}${body.error.slice(1)}.` : `The video server answered ${r.status}.`);
    }
    if (!/mp4/i.test(r.headers.get('content-type') ?? '') || !r.body) {
      await r.body?.cancel();
      throw new Unsupported('not MP4');
    }
    const reader = r.body.getReader();
    const split = new BoxSplitter();
    const running = new Map<number, number>();
    let ftyp: Uint8Array = new Uint8Array(0);
    let init: Init | null = null;
    let moof: Uint8Array | null = null;
    try {
      for (;;) {
        await this.room();
        const { done, value } = await reader.read();
        if (done || this.stopped) return;
        for (const box of split.push(value)) {
          if (box.type === 'ftyp') ftyp = box.bytes;
          else if (box.type === 'moov') {
            init = parseInit(box.bytes);
            if (!init.mime || init.startMs === null || !MediaSource.isTypeSupported(init.mime)) throw new Unsupported(`can't place ${init.mime ?? 'this codec'}`);
            this.anchorMs ??= init.startMs;
            await sink({ kind: 'init', mime: init.mime, bytes: concat(ftyp, box.bytes) });
          } else if (box.type === 'moof') moof = box.bytes;
          else if (box.type === 'mdat' && moof && init) {
            const f = placeFragment(moof, init, init.startMs!, this.anchorMs!, running);
            moof = null;
            if (!f) throw new Unsupported("can't place a fragment");
            await sink({ kind: 'frag', bytes: concat(f.moof, box.bytes), startMs: f.startMs, endMs: f.endMs });
          }
        }
      }
    } finally {
      reader.cancel().catch(() => undefined);
    }
  }

  private async append(it: Item): Promise<void> {
    if (this.stopped) return;
    if (it.kind === 'init') {
      if (!this.sb) {
        this.sb = this.ms.addSourceBuffer(it.mime);
        this.mime = it.mime;
      } else if (it.mime !== this.mime) {
        if (!this.sb.changeType) throw new Unsupported('the codec changed');
        this.sb.changeType(it.mime);
        this.mime = it.mime;
      }
      await this.write(it.bytes);
      return;
    }
    // A stream starts at the keyframe before where it was asked for: what is already buffered adds nothing.
    if (it.endMs <= this.end + JOIN_SLACK_MS) return;
    await this.evict(KEEP_BEHIND_S);
    try {
      await this.write(it.bytes);
    } catch (e) {
      if ((e as DOMException).name !== 'QuotaExceededError') throw e;
      await this.evict(5);
      await this.write(it.bytes);
    }
    this.end = Math.max(this.end, it.endMs);
    // Start where the session asked (the first stream begins at the keyframe before it).
    const target = (this.atMs - (this.anchorMs ?? this.atMs)) / 1000;
    if (!this.seeked && this.video.buffered.length && this.video.buffered.end(this.video.buffered.length - 1) > target) {
      this.seeked = true;
      if (target > 0.05) this.video.currentTime = target;
    }
  }

  private write(bytes: Uint8Array): Promise<void> {
    const sb = this.sb!;
    return new Promise((resolve, reject) => {
      const done = () => {
        sb.removeEventListener('updateend', done);
        sb.removeEventListener('error', bad);
        resolve();
      };
      const bad = () => {
        sb.removeEventListener('updateend', done);
        sb.removeEventListener('error', bad);
        reject(new Error('Media Source refused the video'));
      };
      sb.addEventListener('updateend', done);
      sb.addEventListener('error', bad);
      try {
        sb.appendBuffer(bytes as Uint8Array<ArrayBuffer>);
      } catch (e) {
        sb.removeEventListener('updateend', done);
        sb.removeEventListener('error', bad);
        reject(e);
      }
    });
  }

  /** Drops video more than `keep` seconds behind the playhead. */
  private async evict(keep: number): Promise<void> {
    const sb = this.sb;
    if (!sb || !sb.buffered.length) return;
    const upTo = this.video.currentTime - keep;
    if (upTo - sb.buffered.start(0) < (keep === KEEP_BEHIND_S ? 15 : 0.5)) return;
    await new Promise<void>((resolve) => {
      sb.addEventListener('updateend', () => resolve(), { once: true });
      sb.remove(0, upTo);
    });
  }

  /** Waits while plenty is buffered ahead of the playhead. */
  private async room(): Promise<void> {
    while (!this.stopped && this.sb?.buffered.length && this.sb.buffered.end(this.sb.buffered.length - 1) - this.video.currentTime > MAX_AHEAD_S) await sleep(500);
  }

  /** Steps over a hole in the buffer: video behind the playhead is only ever dropped, so a hole ahead is video NX didn't have. */
  private stepGaps(): void {
    const v = this.video;
    if (v.paused || v.seeking || v.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
      this.stuckSince = null;
      return;
    }
    this.stuckSince ??= Date.now();
    if (!this.done && this.end > 0 && Date.now() - this.stuckSince > STUCK_MS) this.giveUp(new Error('stalled'));
    const t = v.currentTime;
    const b = v.buffered;
    for (let i = 0; i < b.length; i++) {
      if (b.start(i) <= t + 0.1 && b.end(i) > t + 0.1) return; // still inside a range: just buffering
      if (b.start(i) > t) {
        v.currentTime = b.start(i) + 0.05;
        return;
      }
    }
  }

  /** Plays out what is buffered, then 'ended'. */
  private finish(): void {
    this.done = true;
    if (this.ms.readyState === 'open' && this.sb && !this.sb.updating) this.ms.endOfStream();
  }

  private giveUp(e: unknown): void {
    if (this.stopped || this.done || (e as Error).name === 'AbortError') return;
    if (this.end === 0) {
      // Nothing played yet: the server's refusal is the answer; anything else (Media Source refusing NX's video, a
      // codec it can't name) falls back to the plain <video>, which plays the same URL as before this player existed.
      if (e instanceof HttpFail) this.hooks.failed(e.message);
      else this.hooks.unsupported();
      return;
    }
    // Plays out what is buffered, then 'ended': the console carries on from there.
    this.ac.abort();
    this.finish();
  }
}
